import { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { ContractTemplate } from "@/components/Documents/Templates/ContractTemplate";
import { supabase } from "@/integrations/supabase/client";
import { readEdgeFunctionError } from "@/lib/edgeFunctionError";
import { useToast } from "@/hooks/use-toast";
import SignaturePad from "signature_pad";
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  Pen,
  X,
  RefreshCw,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface SignatureData {
  id?: string;
  token?: string;
  contract_id?: string;
  customer_email?: string;
  customer_name?: string;
  customer_document?: string;
  nfe_number?: string;
  nfe_file_url?: string;
  nfe_file_name?: string;
  // L-ASS.1: texto congelado no envio (ausente em envio antigo)
  contract_title?: string;
  contract_content?: string;
  contract_summary?: Record<string, unknown>;
  content_hash?: string;
  status: 'pending' | 'signed' | 'expired' | 'invalidated';
  expires_at: string;
  signed_at?: string;
}

interface ContractData {
  id: string;
  rental_number: string;
  client: string;
  clientDocument: string;
  equipments: Array<{
    type: string;
    gas: string;
    qty: number;
  }>;
  totalValue: number;
  period: string;
  startDate: string;
}

export const SignContract = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [signatureData, setSignatureData] = useState<SignatureData | null>(null);
  const [contractData, setContractData] = useState<ContractData | null>(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [step, setStep] = useState<'review' | 'sign'>('review');
  // L-ASS.1: aceite obrigatório do contrato congelado (envio novo)
  const [accepted, setAccepted] = useState(false);
  // L-ASS.2 (D-ASS.36/39): código de confirmação de 6 dígitos enviado pela Edge send-signature-code
  const [codeSent, setCodeSent] = useState(false);
  const [codeValue, setCodeValue] = useState('');
  const [codeDestination, setCodeDestination] = useState('');
  const [requestingCode, setRequestingCode] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  // m-ASS.23: "Finalizar assinatura" trava durante a conferência; o ref barra o segundo clique antes do re-render
  const [verifying, setVerifying] = useState(false);
  const finalizingRef = useRef(false);

  // L-ASS.2: contagem regressiva do reenvio (1 s); limpa o timer ao desmontar/reiniciar
  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  // Form fields
  const [customerName, setCustomerName] = useState('');
  const [customerDocument, setCustomerDocument] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  
  // Signature pad
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const isRedirectingRef = useRef(false);
  const [isAlreadySigned, setIsAlreadySigned] = useState(false);

  const loadSignatureData = useCallback(async () => {
    if (isRedirectingRef.current || isAlreadySigned) return;

    if (!token) {
      navigate('/');
      return;
    }

    setContractData(null);
    setSignatureData(null);
    setLoading(true);

    try {
      const { data, error } = await supabase.rpc('get_contract_signature_by_token', {
        p_token: token,
        p_ip_address: null,
        p_user_agent: navigator.userAgent,
      });

      if (error) {
        console.error('❌ RPC error:', error);
        toast({
          title: "Erro ao Carregar",
          description: "Não foi possível validar o link de assinatura.",
          variant: "destructive",
        });
        navigate('/');
        return;
      }

      // Rate-limit atingido
      if (data.blocked === true) {
        toast({
          title: "Muitas tentativas",
          description: data.message,
          variant: "destructive",
        });
        navigate('/');
        return;
      }

      // Token não encontrado
      if (data.valid === false) {
        toast({
          title: "Link Inválido",
          description: data.message || "Este link de assinatura não é mais válido. Solicite um novo com a empresa.",
          variant: "destructive",
        });
        navigate('/');
        return;
      }

      const status = data.status as 'pending' | 'signed' | 'expired' | 'invalidated';

      if (status === 'signed') {
        isRedirectingRef.current = true;
        setIsAlreadySigned(true);
        setSignatureData({ status: 'signed', expires_at: data.expires_at, signed_at: data.signed_at });
        setLoading(false);
        setTimeout(() => navigate('/thank-you', { replace: true }), 100);
        return;
      }

      if (status === 'expired') {
        await supabase.rpc('mark_signature_expired_by_token', { p_token: token });
        setSignatureData({ status: 'expired', expires_at: data.expires_at });
        setLoading(false);
        return;
      }

      if (status === 'invalidated') {
        setSignatureData({ status: 'invalidated', expires_at: data.expires_at });
        toast({
          title: "Link de Assinatura Invalidado",
          description: "Esta solicitação de assinatura foi cancelada. Um novo link pode ter sido enviado para o seu e-mail.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // status === 'pending' — payload completo
      const sig = data.signature;
      const rental = data.rental;
      const customer = data.customer;
      const items: Array<{
        quantity: number;
        product_name?: string;
        capacity_liters?: number;
        category?: string;
        brand?: string;
        kind?: string;
      }> = data.items ?? [];

      setSignatureData({
        id:                sig.id,
        token:             sig.token,
        contract_id:       sig.contract_id,
        customer_email:    sig.customer_email,
        customer_name:     sig.customer_name,
        customer_document: sig.customer_document,
        nfe_number:        sig.nfe_number,
        nfe_file_url:      sig.nfe_file_url,
        nfe_file_name:     sig.nfe_file_name,
        contract_title:    sig.contract_title ?? undefined,
        contract_content:  sig.contract_content ?? undefined,
        contract_summary:  sig.contract_summary ?? undefined,
        content_hash:      sig.content_hash ?? undefined,
        status:            'pending',
        expires_at:        sig.expires_at,
        signed_at:         sig.signed_at,
      });
      setCustomerEmail(sig.customer_email || '');

      const equipments = items.map(item => ({
        type: item.product_name ?? 'N/A',
        gas:  '-',
        qty:  item.quantity ?? 1,
      }));

      setContractData({
        id:            rental.id,
        rental_number: rental.rental_number ?? 'N/A',
        client:        customer?.trade_name || customer?.name || customer?.legal_name || 'N/A',
        clientDocument: customer?.document_number || sig.customer_document || '',
        equipments,
        totalValue:    rental.monthly_value ?? 0,
        period:        rental.rental_period ?? 'monthly',
        startDate:     rental.start_date ?? new Date().toISOString(),
      });

    } catch (error) {
      console.error('Erro ao carregar contrato:', error);
      toast({
        title: "Erro",
        description: `Erro ao carregar dados da assinatura: ${(error as Error)?.message || 'Desconhecido'}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [token, navigate, toast, isAlreadySigned]);

  useEffect(() => {
    loadSignatureData();
  }, [loadSignatureData]);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !signaturePadRef.current) return;

    const padData = signaturePadRef.current.isEmpty()
      ? null
      : signaturePadRef.current.toData();

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;

    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(ratio, ratio);

    signaturePadRef.current.clear();
    if (padData) signaturePadRef.current.fromData(padData);
  }, []);

  useEffect(() => {
    if (step !== 'sign') return;
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [step, resizeCanvas]);

  useEffect(() => {
    if (step === 'sign' && canvasRef.current) {
      const timer = setTimeout(() => {
        if (!canvasRef.current) return;
        signaturePadRef.current = new SignaturePad(canvasRef.current, {
          backgroundColor: 'rgb(255, 255, 255)',
          penColor: 'rgb(0, 0, 0)',
          velocityFilterWeight: 0.7,
          minWidth: 0.5,
          maxWidth: 2.5,
        });
        resizeCanvas();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [step, resizeCanvas]);

  const clearSignature = () => {
    signaturePadRef.current?.clear();
  };

  const validateCPF = (cpf: string): boolean => {
    const cleanCPF = cpf.replace(/[^\d]/g, '');
    if (cleanCPF.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(cleanCPF)) return false;
    return true;
  };

  // L-ASS.2: validações comuns a "Receber código" e "Finalizar assinatura" (mesmos toastes de antes)
  const validateBeforeSign = (): boolean => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      toast({ title: "Erro", description: "Por favor, desenhe sua assinatura", variant: "destructive" });
      return false;
    }

    if (!customerName || !customerDocument || !customerEmail) {
      toast({ title: "Erro", description: "Por favor, preencha todos os campos obrigatórios", variant: "destructive" });
      return false;
    }

    if (!validateCPF(customerDocument)) {
      toast({
        title: "Erro",
        description: "Por favor, informe um CPF válido. A assinatura deve ser de pessoa física (11 dígitos).",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  // L-ASS.2: assinatura propriamente dita. code = null só quando a Edge responde not_required (termo de coleta, D-ASS.40)
  const submitSignature = async (code: string | null) => {
    if (!signaturePadRef.current) return;
    setSigning(true);

    try {
      const signatureBase64 = signaturePadRef.current.toDataURL();

      const MAX_SIGNATURE_SIZE = 5 * 1024 * 1024;
      if (signatureBase64.length > MAX_SIGNATURE_SIZE) {
        toast({
          title: "Erro",
          description: "Assinatura muito grande. Tente desenhar de forma mais simples.",
          variant: "destructive",
        });
        return;
      }

      // L-ASS.1: envio novo (texto congelado) manda o aceite; envio antigo segue a assinatura original.
      // L-ASS.2: chamada única; p_code só entra quando há código (types.ts não regenerado: cast).
      const { data: signResult, error } = await (supabase.rpc as any)('sign_contract_by_token', {
        p_token:             token,
        p_customer_name:     customerName,
        p_customer_document: customerDocument,
        p_signature_data:    signatureBase64,
        ...(signatureData?.contract_content ? { p_accepted: true } : {}),
        ...(code ? { p_code: code } : {}),
      });

      // Verificar erro ANTES de checar o resultado (RPC pode lançar exceção de validação)
      if (error) {
        console.error('Supabase sign error:', error);
        throw error;
      }

      if (!signResult) {
        throw new Error(
          code
            ? 'Código inválido ou vencido. Peça um novo código.'
            : 'Token inválido, expirado ou contrato já assinado',
        );
      }

      toast({ title: "✅ Assinado!", description: "Contrato assinado com sucesso!" });

      setTimeout(() => {
        window.location.href = '/thank-you';
      }, 1000);

    } catch (error: any) {
      console.error('Error signing contract:', error);
      const code = error?.code as string | undefined;
      const errorMessage = error?.message || 'Erro desconhecido ao assinar contrato';
      toast({
        title: "Erro",
        description:
          code === '22023'
            ? errorMessage  // mensagem de validação direta do banco (já amigável)
            : errorMessage.includes('row-level security')
            ? "Erro de permissão. Por favor, entre em contato com o suporte."
            : `Erro ao assinar contrato: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setSigning(false);
    }
  };

  // L-ASS.2: pede o código à Edge pública send-signature-code (portão no token)
  const handleRequestCode = async () => {
    if (!validateBeforeSign()) return;
    setRequestingCode(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-signature-code', {
        body: { token },
      });

      if (!error) {
        setCodeSent(true);
        setCodeDestination(data?.destination_masked ?? '');
        setCodeValue('');
        setResendIn(60);
        toast({ title: "Código enviado", description: `Enviamos o código para ${data?.destination_masked ?? 'seu contato'}.` });
        return;
      }

      // Recusa non-2xx: corpo cru { error, reason, retry_after_seconds }. Lido por uma cópia da resposta,
      // para que o readEdgeFunctionError de reserva ainda consiga ler o corpo original.
      let body: { error?: string; reason?: string; retry_after_seconds?: number } | null = null;
      try {
        body = await (error as any)?.context?.clone?.()?.json?.();
      } catch {
        body = null;
      }

      if (body?.reason === 'not_required') {
        // D-ASS.40: termo de coleta (e demais sem código) assina como sempre
        await submitSignature(null);
        return;
      }

      if (body?.reason === 'wait') {
        setCodeSent(true);
        setResendIn(body.retry_after_seconds ?? 60);
        toast({ title: "Aguarde", description: body.error ?? 'Aguarde antes de pedir um novo código.' });
        return;
      }

      const message = body?.error ?? (await readEdgeFunctionError(error));
      toast({ title: "Erro", description: message, variant: "destructive" });
    } catch (error: any) {
      console.error('Erro ao pedir código de confirmação:', error);
      toast({
        title: "Erro",
        description: error?.message || 'Não foi possível enviar o código. Tente novamente.',
        variant: "destructive",
      });
    } finally {
      setRequestingCode(false);
    }
  };

  // L-ASS.2: confere o código no banco e, se válido, assina
  const handleFinalize = async () => {
    // m-ASS.23: um clique por vez — clique duplo não confere (nem gasta tentativa) duas vezes
    if (finalizingRef.current) return;

    if (!validateBeforeSign()) return;

    if (!/^\d{6}$/.test(codeValue)) {
      toast({ title: "Erro", description: "Digite o código de 6 dígitos", variant: "destructive" });
      return;
    }

    finalizingRef.current = true;
    setVerifying(true);

    try {
      const { data, error } = await (supabase.rpc as any)('verify_contract_signature_code', {
        p_token: token,
        p_code:  codeValue,
      });

      if (error) {
        console.error('Erro ao verificar código:', error);
        toast({ title: "Erro", description: "Não foi possível verificar o código. Tente novamente.", variant: "destructive" });
        return;
      }

      if (data?.ok !== true) {
        const reason = data?.reason as string | undefined;
        const description =
          reason === 'wrong'   ? `Código incorreto. Restam ${data?.attempts_left ?? 0} tentativa(s).` :
          reason === 'expired' ? 'Código vencido. Peça um novo código.' :
          reason === 'blocked' ? 'Código bloqueado por excesso de tentativas. Peça um novo código.' :
          reason === 'none'    ? 'Peça o código antes de finalizar.' :
          reason === 'invalid' ? 'Link inválido ou expirado.' :
                                 'Código não aceito. Peça um novo código.';
        toast({ title: "Erro", description, variant: "destructive" });
        return;
      }

      setVerifying(false);
      await submitSignature(codeValue);
    } finally {
      finalizingRef.current = false;
      setVerifying(false);
    }
  };

  const getPeriodLabel = (period: string) => {
    switch (period) {
      case 'monthly':     return 'Mensal';
      case 'quarterly':   return 'Trimestral';
      case 'semi_annual': return 'Semestral';
      case 'annual':      return 'Anual';
      default:            return 'Mensal';
    }
  };

  const handleReloadData = () => {
    window.location.reload();
  };

  if (isAlreadySigned || signatureData?.status === 'signed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-success/5 to-success/10 p-4">
        <Card className="w-full max-w-md text-center p-8">
          <div className="flex flex-col items-center gap-4">
            <CheckCircle className="h-16 w-16 text-success animate-pulse" />
            <h2 className="text-2xl font-bold text-success">✅ Contrato Já Assinado!</h2>
            <p className="text-muted-foreground">Redirecionando para página de confirmação...</p>
            <Button onClick={() => navigate('/thank-you', { replace: true })} className="mt-4">
              Ir para Confirmação
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center space-x-2">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span>Carregando...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!signatureData || (signatureData.status === 'pending' && !contractData)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>Link de assinatura inválido ou expirado</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signatureData.status === 'invalidated') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl border-yellow-500/50">
          <CardHeader>
            <div className="mx-auto w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="h-8 w-8 text-yellow-600" />
            </div>
            <CardTitle className="text-2xl text-center text-yellow-900 dark:text-yellow-200">
              Link de Assinatura Cancelado
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Esta solicitação de assinatura foi cancelada pelo proprietário do contrato.
                <br />
                <strong>Motivo comum:</strong> O contrato pode ter sido editado ou atualizado.
              </AlertDescription>
            </Alert>

            <div className="space-y-2 text-sm text-muted-foreground">
              <p><strong>O que fazer?</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Verifique seu e-mail para um novo link de assinatura</li>
                <li>Entre em contato com a empresa que enviou o contrato</li>
                <li>Se você acabou de receber este link, ele pode ter sido substituído</li>
              </ul>
            </div>

            {signatureData.customer_email && (
              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  <strong>E-mail do cliente:</strong> {signatureData.customer_email}
                </p>
                {signatureData.nfe_number && (
                  <p className="text-sm text-muted-foreground mt-1">
                    <strong>NFe:</strong> {signatureData.nfe_number}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signatureData.status === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-destructive/5 to-destructive/10 p-4">
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <Clock className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle className="text-2xl text-destructive">Link Expirado</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              O prazo para assinatura deste contrato expirou em{' '}
              {format(new Date(signatureData.expires_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
            </p>
            <p className="text-sm text-muted-foreground">
              Entre em contato conosco para solicitar um novo link de assinatura.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // status === 'pending' — contractData garantidamente não-null aqui
  const contract = contractData!;
  // L-ASS.1: envio novo traz o contrato inteiro congelado; envio antigo mostra os blocos resumidos
  const hasFrozenContract = !!signatureData.contract_content;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl">Assinatura Digital do Contrato</CardTitle>
                  <p className="text-muted-foreground">
                    Contrato {contract.rental_number} • Expira em{' '}
                    {format(new Date(signatureData.expires_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={handleReloadData} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                <span className="hidden sm:inline">Recarregar</span>
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Steps indicator */}
        <div className="flex items-center justify-center space-x-4">
          <div className={`flex items-center space-x-2 ${step === 'review' ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'review' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
              <FileText className="h-4 w-4" />
            </div>
            <span className="hidden sm:inline">Revisar Contrato</span>
          </div>
          <div className="w-12 h-px bg-border" />
          <div className={`flex items-center space-x-2 ${step === 'sign' ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'sign' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
              <Pen className="h-4 w-4" />
            </div>
            <span className="hidden sm:inline">Assinar</span>
          </div>
        </div>

        {step === 'review' && (
          <Card>
            <CardHeader>
              <CardTitle>Revisão do Contrato</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6" id="contract-preview">
              {hasFrozenContract ? (
                /* L-ASS.1: contrato inteiro, exatamente como congelado no envio */
                <div className="space-y-2">
                  <div className="border rounded-lg max-h-[70vh] overflow-y-auto bg-muted/30">
                    <ContractTemplate
                      data={signatureData.contract_summary as unknown as Parameters<typeof ContractTemplate>[0]['data']}
                      title={signatureData.contract_title}
                      content={signatureData.contract_content}
                    />
                  </div>
                  {signatureData.content_hash && (
                    <p className="text-xs text-muted-foreground break-all">
                      Código de verificação do documento: {signatureData.content_hash}
                    </p>
                  )}
                </div>
              ) : (
                <>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="font-medium">Detalhes do Cliente</h4>
                  <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                    <p><strong>Nome/Razão Social:</strong> {contract.client}</p>
                    <p><strong>CPF/CNPJ:</strong> {contract.clientDocument}</p>
                    <p><strong>E-mail:</strong> {signatureData.customer_email}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium">Detalhes do Contrato</h4>
                  <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                    <p><strong>Período:</strong> {getPeriodLabel(contract.period)}</p>
                    <p><strong>Valor Total:</strong> {contract.totalValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                    <p><strong>Início:</strong> {format(new Date(contract.startDate), "dd/MM/yyyy", { locale: ptBR })}</p>
                  </div>
                </div>
              </div>

              {/* Equipment list */}
              <div className="space-y-3">
                <h4 className="font-medium">Itens Locados</h4>
                <div className="bg-muted/50 p-4 rounded-lg">
                  {contract.equipments.length > 0 ? (
                    contract.equipments.map((equipment, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                        <div>
                          <span className="font-medium">{equipment.qty}x {equipment.type}</span>
                          {equipment.gas && equipment.gas !== "-" && (
                            <span className="text-muted-foreground ml-2">({equipment.gas})</span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-sm">Nenhum item encontrado</p>
                  )}
                </div>
              </div>
                </>
              )}

              {/* NFe */}
              {signatureData.nfe_file_url && (
                <div className="space-y-3 pt-4">
                  <h4 className="font-medium">📎 Documentos Anexados</h4>
                  <div className="bg-green-50 border-2 border-green-200 p-4 rounded-lg space-y-3">
                    <Button variant="outline" className="w-full justify-start" asChild>
                      <a href={signatureData.nfe_file_url} target="_blank" rel="noopener noreferrer">
                        <FileText className="h-4 w-4 mr-2" />
                        Download NFe Nº {signatureData.nfe_number}
                        {signatureData.nfe_file_name && ` - ${signatureData.nfe_file_name}`}
                      </a>
                    </Button>
                    <p className="text-xs text-muted-foreground">Clique para baixar a Nota Fiscal Eletrônica</p>
                  </div>
                </div>
              )}

              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Importante:</strong> Ao prosseguir, você concorda com todos os termos e condições deste contrato de locação.
                  Por favor, leia atentamente todas as cláusulas antes de assinar.
                </AlertDescription>
              </Alert>

              {hasFrozenContract && (
                <div className="flex items-start gap-3 rounded-lg border p-4">
                  <Checkbox
                    id="accept-contract"
                    checked={accepted}
                    onCheckedChange={(checked) => setAccepted(checked === true)}
                    className="mt-0.5"
                  />
                  <Label htmlFor="accept-contract" className="text-sm leading-snug cursor-pointer">
                    Li o contrato acima e concordo com todas as suas cláusulas e condições.
                  </Label>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <Button onClick={() => setStep('sign')} size="lg" disabled={hasFrozenContract && !accepted}>
                  Prosseguir para Assinatura
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 'sign' && (
          <Card>
            <CardHeader>
              <CardTitle>Dados Pessoais e Assinatura</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome/Razão Social *</Label>
                  <Input
                    id="name"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Nome completo ou razão social"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="document">CPF *</Label>
                  <Input
                    id="document"
                    value={customerDocument}
                    onChange={(e) => setCustomerDocument(e.target.value)}
                    placeholder="Digite seu CPF (apenas números)"
                    maxLength={11}
                  />
                  <p className="text-xs text-muted-foreground">
                    Apenas CPF (11 dígitos). Assinatura de pessoa física.
                  </p>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="email">E-mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="seu@email.com"
                  />
                </div>
              </div>

              {/* Signature pad */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Assinatura Digital *</Label>
                  <Button variant="outline" size="sm" onClick={clearSignature}>
                    <X className="h-4 w-4 mr-1" />
                    Limpar
                  </Button>
                </div>
                <div className="border-2 border-dashed border-border rounded-lg p-4">
                  <canvas
                    ref={canvasRef}
                    className="w-full h-32 border border-border rounded bg-white cursor-crosshair touch-none"
                    style={{ touchAction: 'none' }}
                  />
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    Desenhe sua assinatura no campo acima
                  </p>
                </div>
              </div>

              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  Ao assinar digitalmente, você confirma que leu e concorda com todos os termos do contrato.
                  Sua assinatura digital tem validade jurídica equivalente à assinatura manuscrita.
                </AlertDescription>
              </Alert>

              {/* L-ASS.2 (D-ASS.36/39): bloco do código entra como irmão — a árvore do canvas acima não muda */}
              {codeSent && (
                <div className="space-y-3 rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">
                    Enviamos um código de 6 dígitos para {codeDestination}. Ele vale por 10 minutos.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="signature-code">Código de confirmação</Label>
                    <Input
                      id="signature-code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={codeValue}
                      onChange={(e) => setCodeValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="link"
                    className="px-0"
                    onClick={handleRequestCode}
                    disabled={resendIn > 0 || requestingCode}
                  >
                    {resendIn > 0 ? `Reenviar código (${resendIn}s)` : 'Reenviar código'}
                  </Button>
                </div>
              )}

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep('review')}>
                  Voltar
                </Button>
                {!codeSent ? (
                  <Button onClick={handleRequestCode} disabled={signing || requestingCode} size="lg">
                    {signing ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Processando...
                      </>
                    ) : requestingCode ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Pen className="h-4 w-4 mr-2" />
                        Receber código
                      </>
                    )}
                  </Button>
                ) : (
                  <Button onClick={handleFinalize} disabled={signing || verifying || codeValue.length !== 6} size="lg">
                    {signing ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Processando...
                      </>
                    ) : verifying ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Conferindo...
                      </>
                    ) : (
                      <>
                        <Pen className="h-4 w-4 mr-2" />
                        Finalizar assinatura
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
